import { Controller } from "@hotwired/stimulus"
import {
  createLineChart,
  destroyChart,
  contributionsAndGrowthDatasets
} from "../modules/money_smart/chart_helpers"

export default class extends Controller {
  static targets = [
    "form", "principal", "annualRate", "years",
    "compoundingFrequency", "monthlyContribution",
    "submitButton", "results", "errors", "errorMessage",
    "finalAmount", "totalContributions", "totalInterest",
    "chart", "breakdownBody", "printButton"
  ]

  static values = { calculateUrl: String, autoCalculate: Boolean }

  chart = null

  connect() {
    this.prefillFromUrl()
    if (this.autoCalculateValue && !new URLSearchParams(window.location.search).size) {
      this.formTarget.requestSubmit()
    }
  }

  disconnect() {
    destroyChart(this.chart)
    this.chart = null
  }

  async calculate(event) {
    event.preventDefault()
    this.hideErrors()

    const body = {
      principal: this.principalTarget.value,
      annual_rate: this.annualRateTarget.value,
      years: this.yearsTarget.value,
      compounding_frequency: this.compoundingFrequencyTarget.value,
      monthly_contribution: this.monthlyContributionTarget.value
    }

    this.submitButtonTarget.disabled = true

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (!response.ok) {
        this.showErrors(data.errors || ["Calculation failed"])
        return
      }

      this.renderResults(data)
      this.updateUrl(body)
    } catch {
      this.showErrors(["Something went wrong. Please try again."])
    } finally {
      this.submitButtonTarget.disabled = false
    }
  }

  renderResults(data) {
    this.finalAmountTarget.textContent = this.formatMoney(data.final_amount)
    this.totalContributionsTarget.textContent = this.formatMoney(data.total_contributions)
    this.totalInterestTarget.textContent = this.formatMoney(data.total_interest)

    this.renderBreakdown(data.yearly_breakdown)
    this.renderChart(data.chart_data)

    this.resultsTarget.classList.remove("hidden")
  }

  renderBreakdown(rows) {
    const filtered = rows.filter(r => r.year > 0)
    this.breakdownBodyTarget.innerHTML = filtered.map(row => `
      <tr class="border-b border-stone-100 hover:bg-stone-50">
        <td class="px-4 py-2 text-stone-700">${row.year}</td>
        <td class="px-4 py-2 text-right money-value">${this.formatMoney(row.balance)}</td>
        <td class="px-4 py-2 text-right text-stone-600">${this.formatMoney(row.total_contributions)}</td>
        <td class="px-4 py-2 text-right text-emerald-600">${this.formatMoney(row.interest_this_year)}</td>
        <td class="px-4 py-2 text-right text-emerald-600">${this.formatMoney(row.total_interest)}</td>
      </tr>
    `).join("")
  }

  async renderChart(chartData) {
    destroyChart(this.chart)
    this.chart = null

    const datasets = contributionsAndGrowthDatasets(chartData)
    this.chart = await createLineChart(
      this.chartTarget, chartData.labels, datasets
    )
  }

  updateUrl(params) {
    const url = new URL(window.location)
    Object.entries(params).forEach(([key, val]) => {
      if (val !== "" && val !== null && val !== undefined) {
        url.searchParams.set(key, val)
      } else {
        url.searchParams.delete(key)
      }
    })
    window.history.replaceState({}, "", url)
  }

  prefillFromUrl() {
    const params = new URLSearchParams(window.location.search)
    if (params.size === 0) return

    const fields = {
      principal: this.principalTarget,
      annual_rate: this.annualRateTarget,
      years: this.yearsTarget,
      compounding_frequency: this.compoundingFrequencyTarget,
      monthly_contribution: this.monthlyContributionTarget
    }

    let hasValues = false
    Object.entries(fields).forEach(([key, el]) => {
      const val = params.get(key)
      if (val !== null) {
        el.value = val
        hasValues = true
      }
    })

    if (hasValues) {
      this.formTarget.requestSubmit()
    }
  }

  print() {
    window.print()
  }

  showErrors(messages) {
    this.resultsTarget.classList.add("hidden")
    this.errorMessageTarget.textContent = messages.join(". ")
    this.errorsTarget.classList.remove("hidden")
  }

  hideErrors() {
    this.errorsTarget.classList.add("hidden")
  }

  formatMoney(value) {
    return "$" + Number(value).toLocaleString("en-NZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
